<script setup>
import AnButton from "@/components/common/AnButton.vue";
import AnInput from "@/components/common/AnInput.vue";
import AnLoader from "@/components/common/AnLoader.vue";
import { goalOptions } from "@/services/constant-services";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";

const props = defineProps({
  setSection: {
    type: Function,
    required: true,
  },
});

const store = useUserStore();
const goalDetails = computed(() => store.dashboardData.custGoals);
const alreadySetGoals = computed(
  () => store.dashboardData.custGoals || []
).value.map((item) => item.goalName);
const updatedGoals = goalOptions.map((item) => {
  return {
    ...item,
    alreadySet: alreadySetGoals.includes(item.label),
  };
});
const goalAmount = ref(null);
const selectedGoal = ref(null);
const targetDate = ref(null);
const goalStatus = ref("Pending");

const setGoal = async (action) => {
  if (selectedGoal.value && targetDate.value && goalAmount.value) {
    const payload = {
      targetDate: new Date(targetDate.value).toISOString().split("T")[0],
      goalName: selectedGoal?.value.label,
      goalTargetAmount: goalAmount.value,
      goalAction: action,
    };

    try {
      const success = await store.setGoal(payload);
      if (success) {
        console.log('Goal operation completed successfully');

        // Clear form after successful operation
        selectedGoal.value = null;
        goalAmount.value = null;
        targetDate.value = null;

        // Show success message with action-specific text
        const actionText = action === 'create' ? 'created' : action === 'update' ? 'updated' : 'deleted';
        store.fireAlert('Success', `Goal ${actionText} successfully! Click "Back" to view dashboard.`);

        // DO NOT auto-navigate - let user click Back button manually
        // This prevents chart rendering errors by giving data time to load
      }
    } catch (error) {
      console.error('Error setting goal:', error);
      store.fireAlert("Error", "Failed to process goal. Please try again.");
    }
  } else {
    store.fireAlert("Warning", "Please fill all fields");
  }
};

const autoPopulate = () => {
  if (alreadySetGoals.includes(selectedGoal?.value?.label)) {
    const goal = goalDetails.value.filter(
      (item) => item.goalName === selectedGoal?.value?.label
    );
    goalAmount.value = goal[0].goalTargetAmount;
    targetDate.value = goal[0].goalDueDate;
  } else if (selectedGoal?.value === null) {
    goalAmount.value = null;
    targetDate.value = null;
  }
};
</script>

<template>
  <AnLoader v-if="store.isDashboardLoading" />
  <form v-else>
    <div class="input-shadow w-100 p-10 pb-20 mt-20 br-10">
      <div class="d-flex w-100 gap-20">
        <AnInput
          type="select"
          label="Goal Name"
          v-model="selectedGoal"
          :options="updatedGoals"
          optionLabel="label"
          placeholder="Select a Goal"
          wrapper-class="w-50"
          :onSelectChange="(value) => autoPopulate(value)"
        />
        <AnInput
          placeholder="Enter target date"
          label="Target Date"
          v-model="targetDate"
          type="calendar"
          wrapper-class="w-50"
        />
      </div>

      <AnInput
        label="Target Goal Amount"
        placeholder="Enter target goal amount"
        v-model="goalAmount"
        type="currency"
        required
        :is-dark="false"
      />

      <AnInput
        label="Goal Status"
        placeholder="Status"
        v-model="goalStatus"
        default="Pending"
        type="text"
        disabled
        :is-dark="false"
      />
    </div>
    <div class="d-flex justify-center gap-20 mt-30">
      <AnButton
        label="Back"
        @click="() => props.setSection(1)"
        variant="tab"
        :is-disabled="store.isDashboardLoading"
      />
      <AnButton
        label="Update Goal"
        :is-disabled="store.isDashboardLoading"
        type="submit"
        variant="primary"
        @click.prevent="() => setGoal('update')"
        v-if="alreadySetGoals.includes(selectedGoal?.label)"
      />
      <AnButton
        label="Calculate Now"
        :is-disabled="store.isDashboardLoading"
        type="submit"
        variant="primary"
        @click.prevent="() => setGoal('create')"
        v-else
      />
      <AnButton
        label="Delete Goal"
        :is-disabled="store.isDashboardLoading"
        variant="error"
        v-if="alreadySetGoals.includes(selectedGoal?.label)"
        @click.prevent="() => setGoal('delete')"
      />
    </div>
  </form>
</template>

<style scoped></style>
